/* A technician's day as Mangomint has it: when they are in, when they go, and
 * the time blocked off in between.
 *
 * `shift.js` is the day as the DESK has it — two times typed into a menu, and
 * defaults that change nothing until somebody does. This is the other source,
 * read off Mangomint's calendar by the minute poll (schema part 17), and the
 * rules for how the two meet and what the rotation does with the result.
 *
 * Kevin, 2026-10-22:
 *
 *   ON THE BOARD   everyone Mangomint has working today goes on the board when
 *                  the day opens. Somebody who starts later is on it too, out
 *                  of the rotation and badged with their start, until then.
 *   NOT IN         the rotation's squares that go by before they are in are
 *                  charged, the same as a late arrival's missed turns always
 *                  were: the count is what the floor had finished by their
 *                  start time. A one o'clock start does not leapfrog a floor
 *                  that has worked since ten.
 *   TIME BLOCKS    a break. While one is running they are out of the rotation
 *                  as Break, and charged exactly as a Break is — a red square
 *                  when the rotation actually reaches them. They come back by
 *                  themselves when it ends.
 *
 * Kevin, 2026-10-24, after Jocy (in 10 to 7, blocked 2 to 8) was offered a
 * full service at half one:
 *
 *   BLOCK AHEAD    a time block coming up is a cutoff for new work, the same
 *                  as an out time: nothing is handed to them that would run
 *                  into it. Work that fits after it ends still can be.
 *   BLOCK TO END   a block that runs to the end of their shift, or past it,
 *                  ends their day where it starts. It is their out time, not a
 *                  break: Hours today says so, and it sends them home.
 *   MOVED START    the not-in count is worked out again from the floor's turns
 *                  as of the new start, and their arrival moves with it. Turns
 *                  they actually took stay where they are.
 *   WHO WINS       Mangomint fills Hours today. A time typed at the desk stands
 *                  until Mangomint's time for that person changes again, and a
 *                  Mangomint out time counts as typed — it sends them home.
 *   BOOKED PAST    a service Mangomint has booked on them that runs past their
 *                  out time is still theirs: it is not a conflict, it can go
 *                  up on their column, and they are not sent home before it.
 *
 * With no Mangomint data for somebody, every function here hands back the
 * answer that changes nothing, so a salon without the feature has the board it
 * had before.
 *
 * Times are milliseconds throughout; the store converts Mangomint's wall-clock
 * once, in the scheduling plugin.
 */
import { buildSlots, creditFrom } from './turns.js';

/* One technician's Mangomint day, from their shift spans and time blocks.
 *
 *   spans   [{ from, to }]   their working hours, one or more spans
 *   blocks  [{ id, from, to, label }]
 *   changedAt                when the poll last saw their hours change
 *
 * A split shift is one day with a break in it: the gap between two spans is
 * time they are not there, which is the same thing to the rotation as a block.
 * Null for nobody — no spans means Mangomint does not have them working.
 *
 * A block that starts after they are in and runs to the end of the day, or
 * past it, is where the day ends (BLOCK TO END). It, and anything that would
 * have come after it, is then no break: there is nothing to come back to.
 */
export function mangomintDay(spans, blocks, changedAt) {
  var list = (spans || []).filter(function (s) { return s && s.to > s.from; })
    .slice().sort(function (a, b) { return a.from - b.from; });
  if (!list.length) return null;
  var startAt = list[0].from;
  var endAt = list[list.length - 1].to;
  var good = (blocks || []).filter(function (b) { return b && b.to > b.from; });
  good.forEach(function (b) {
    if (b.from > startAt && b.from < endAt && b.to >= list[list.length - 1].to) {
      endAt = b.from;
    }
  });
  var breaks = [];
  for (var i = 1; i < list.length; i++) {
    if (list[i].from > list[i - 1].to) {
      breaks.push({ id: 'gap:' + list[i - 1].to, from: list[i - 1].to, to: list[i].from,
                    label: 'Between shifts' });
    }
  }
  good.forEach(function (b) {
    breaks.push({ id: 'block:' + b.id, from: b.from, to: b.to, label: b.label || 'Break' });
  });
  breaks = breaks.filter(function (b) { return b.from < endAt; });
  breaks.sort(function (a, b) { return a.from - b.from; });
  return {
    startAt: startAt,
    endAt: endAt,
    changedAt: changedAt == null ? null : changedAt,
    breaks: breaks
  };
}

/* Which in and out time the board works to, and where each came from.
 *
 *   typed    { startAt, endAt, typedAt } off the day-staff row — what the desk
 *            typed, either end null when it did not
 *   mm       mangomintDay(), or null
 *
 * Each end on its own: the desk typing only an out time leaves Mangomint's in
 * time standing. The desk's end wins when it typed one and Mangomint has not
 * changed since; `typedAt` null on a row from before this existed counts as
 * typed before anything Mangomint has said.
 *
 * `from` is 'desk', 'mangomint' or null — null meaning neither said, and the
 * caller falls back to the defaults in shift.js.
 */
export function effectiveHours(typed, mm) {
  var t = typed || {};
  var deskCurrent = !mm || mm.changedAt == null ||
    (t.typedAt != null && t.typedAt >= mm.changedAt);
  function side(typedAt, mmAt) {
    if (typedAt != null && deskCurrent) return { at: typedAt, from: 'desk' };
    if (mm && mmAt != null) return { at: mmAt, from: 'mangomint' };
    if (typedAt != null) return { at: typedAt, from: 'desk' };
    return { at: null, from: null };
  }
  var s = side(t.startAt, mm && mm.startAt);
  var e = side(t.endAt, mm && mm.endAt);
  return { startAt: s.at, startFrom: s.from, endAt: e.at, endFrom: e.from };
}

/* Whether Mangomint's day has them out of the rotation right now, and why.
 *
 *   startAt    when they are in — only when Mangomint has a day for them, so a
 *              time typed with no Mangomint data never holds anybody out
 *   breaks     mangomintDay().breaks
 *   released   keys the desk has brought them back from early ("Back in the
 *              rotation" while one was running)
 *
 * Returns null, or { reason: 'notin' | 'break', key, until, label }.
 */
export function outOfRotationAt(startAt, breaks, released, at) {
  var done = {};
  (released || []).forEach(function (k) { done[k] = true; });
  if (startAt != null && at < startAt) {
    var key = 'in:' + startAt;
    if (!done[key]) return { reason: 'notin', key: key, until: startAt, label: 'Not in yet' };
  }
  var list = breaks || [];
  for (var i = 0; i < list.length; i++) {
    var b = list[i];
    if (at >= b.from && at < b.to && !done[b.id]) {
      return { reason: 'break', key: b.id, until: b.to, label: b.label };
    }
  }
  return null;
}

/* The order the day opens in (Kevin, 2026-10-25): by when each technician is
 * in, ties alphabetically. On a fresh morning everybody is on zero turns, so
 * this is the rotation — whoever is in first is up first.
 *
 *   crew   [{ id, name, startAt }] — startAt their in time where it is known
 *          (Mangomint's shift, or one the desk typed), null where it is not
 *
 * Anybody with no known in time goes after everybody who has one, in the order
 * they were given: the board has nothing to place them by. A new list; the one
 * passed in is left as it was.
 */
export function openingOrder(crew) {
  var known = [], unknown = [];
  (crew || []).forEach(function (c) { (c.startAt == null ? unknown : known).push(c); });
  known.sort(function (a, b) {
    return a.startAt - b.startAt ||
      String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' });
  });
  return known.concat(unknown);
}

/* The roster with the crew in `orderedIds` order, everybody else where they
 * were: the crew's own places are refilled in the new order. Somebody not in
 * today does not move, so tomorrow's roster is only as different as today's
 * opening made it. */
export function placeInRoster(rosterIds, orderedIds) {
  var inCrew = {};
  (orderedIds || []).forEach(function (id) { inCrew[id] = true; });
  var next = 0;
  return (rosterIds || []).map(function (id) {
    return inCrew[id] ? orderedIds[next++] : id;
  });
}

/* The soonest somebody held out of the rotation could start work, asked at
 * `at` — for Next availability, which is a question about the next hour and
 * not only about this minute (Kevin, 2026-10-25: Nancy in at ten could take a
 * walk-in at ten).
 *
 *   hold   outOfRotationAt()'s answer — not in yet, or a time block — or null
 *          for somebody the desk has bypassed, whose Break or Done ends when
 *          a person says so and not at a time the board can name.
 *
 * Null when there is no time to give.
 */
export function startAfterHold(at, hold) {
  if (!hold || hold.until == null) return null;
  return hold.until > at ? hold.until : at;
}

/* When somebody will be in and ready, for a question about a time ahead — the
 * conflicts list asking whether a booking has its technician (Kevin,
 * 2026-11-02: assume they are in and ready at the time they are scheduled
 * for). Unlike `outOfRotationAt`, an in time typed at the desk counts here with
 * or without Mangomint: it holds nobody out of the rotation, but it is still
 * the desk saying when they arrive.
 *
 *   startAt    their in time where somebody gave one — Mangomint's shift, or
 *              one typed at the desk — and null where the board only assumes
 *   released   as for outOfRotationAt: brought back ahead of their start
 *
 * Null when there is nothing to wait for.
 */
export function readyFrom(startAt, released) {
  if (startAt == null) return null;
  return (released || []).indexOf('in:' + startAt) === -1 ? startAt : null;
}

/* The break that work running from `from` to `to` would run into, or null
 * (BLOCK AHEAD). The first one in the way, so a caller can try again from
 * where it ends. One already running counts — the work cannot start in it —
 * and one the desk brought them back from does not.
 *
 *   breaks     mangomintDay().breaks
 *   released   as for outOfRotationAt
 */
export function breakInWay(breaks, released, from, to) {
  var done = {};
  (released || []).forEach(function (k) { done[k] = true; });
  var list = breaks || [];
  for (var i = 0; i < list.length; i++) {
    var b = list[i];
    if (!done[b.id] && b.from < to && b.to > from) return b;
  }
  return null;
}

/* The floor's finished turns as they stood at `at` — what somebody whose day
 * starts then has missed.
 *
 * The same number `floorTurnsCompleted` reads off the floor live — the lowest
 * credit among everybody working, rounded down to the row the next turn lands
 * in — but worked out of the day's history, so it can be asked of a start time
 * that has already gone by. That is a moved start (Kevin: worked out again as
 * of the new start), and it is also a station that was switched off at one
 * o'clock when somebody was due in.
 *
 * `members` are the other technicians on the board:
 *   { arrivedAt, missedTurns, turns: [{ turnWeight, startedAt, endedAt }]
 *     (in column order), skips: [square index], outSpans: [{ from, to }] }
 *
 * Somebody counts if they had arrived by `at` and were not out of the rotation
 * at that moment — the same people the live number takes its minimum over.
 * Their credit is their own missed turns, the squares they had started by
 * then, and the red squares recorded before the first of those. A skip is
 * stored as the square it was charged at, not a time, so a pass recorded at
 * the very square they had reached by `at` is counted only when `at` is now:
 * it might have come after.
 */
export function floorTurnsAt(members, at, now) {
  return floorTurnsDetail(members, at, now).count;
}

/* The same count, with the floor it was read off: for each member their
 * `name`, whether they were counted, why not (`'not in yet'` or `'out'`) and
 * the credit they were counted at. Kept beside a missed count on the day, so
 * a count nobody expected can be traced to what the station saw (Kevin,
 * 2026-10-25). */
export function floorTurnsDetail(members, at, now) {
  var min = Infinity;
  var lines = (members || []).map(function (m) {
    var line = { name: m.name == null ? null : m.name, counted: false, why: null, credit: null };
    if (m.arrivedAt == null || m.arrivedAt > at) { line.why = 'not in yet'; return line; }
    var out = (m.outSpans || []).some(function (s) {
      return s.from <= at && (s.to == null || s.to > at);
    });
    if (out) { line.why = 'out'; return line; }
    var started = (m.turns || []).filter(function (t) { return t.startedAt <= at; });
    var slots = buildSlots(started);
    var reach = slots.length;
    var live = now != null && at >= now;
    var skips = (m.skips || []).filter(function (i) { return live ? i <= reach : i < reach; }).length;
    var credit = creditFrom(slots, m.missedTurns || 0, skips).credit;
    if (credit < min) min = credit;
    line.counted = true;
    line.credit = credit;
    return line;
  });
  return { count: min === Infinity ? 0 : Math.max(0, Math.floor(min)), members: lines };
}

/* What a Mangomint start coming changes on their day row (Kevin, 2026-10-25):
 * the missed count, worked out as of that start, and a note of the start it
 * was worked out for and how (`record`). Nothing else. After the day starts
 * the desk's column order is authoritative — techs may well have arrived at
 * about the same time — so the start moves neither their arrival time nor
 * their column.
 *
 *   w    their workHours as it stands */
export function startArrived(w, startAt, missed, record) {
  var x = w || {};
  return {
    missedTurns: missed,
    workHours: {
      typedAt: x.typedAt == null ? null : x.typedAt,
      missedAt: startAt,
      released: (x.released || []).slice(),
      missedWhy: record || null
    }
  };
}

/* Whether the not-in count wants working out (again): their start has come,
 * and it is not the start the count was last worked out for. */
export function missedDue(startAt, missedAt, at) {
  if (startAt == null || at < startAt) return false;
  return missedAt !== startAt;
}

/* When the board may send them home: their out time, or the end of the last
 * service Mangomint has booked on them, whichever is later. Nobody is marked
 * Done for today with a client still due in their chair. */
export function homeAt(endAt, bookedEnds) {
  if (endAt == null) return null;
  var out = endAt;
  (bookedEnds || []).forEach(function (e) { if (e != null && e > out) out = e; });
  return out;
}

/* Whether a booking running to `to` is "gone by then" for this technician.
 *
 * It is not when Mangomint set the out time and has the booking on them
 * itself: the book and the hours came from the same place, and Mangomint took
 * the booking knowing both. A time typed at the desk is the desk knowing
 * something Mangomint does not, so a booking past it is still worth saying. */
export function goneByThen(cut, to, endFrom, bookedOnThem) {
  if (cut == null || !(to > cut)) return false;
  if (endFrom === 'mangomint' && bookedOnThem) return false;
  return true;
}
